import React, { MutableRefObject, RefObject, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import accessCheck from '../../../hooks/accessCheck';
import { SmxPermissionContext } from '../../../providers/permissionProvider';
import { ComponentsDispatch } from '../../../store';
import { ItemType, MappingItemIdIndexType } from '../../../types';
import useControlLeftHeader from '../../useControlLeftHeader';
import { DragStrategy } from '../drag/dragStrategy';
import { DragStrategyBetween } from '../drag/dragStrategyBetween';
import { DragStrategyParent } from '../drag/dragStrategyParent';

export const useProjectsTreeDrag = (
    rowsMap: Map<string, RefObject<HTMLDivElement>[]>,
    popoverRef: MutableRefObject<any>,
    itemsList: ItemType[],
    mappingItemIdIndex: MappingItemIdIndexType,
) => {
    const { startDragging, finishDragging } = useControlLeftHeader();
    const dispatch = useDispatch<ComponentsDispatch>();
    const { userId, permissionListScore } = useContext(SmxPermissionContext);

    return useMemo(() => {
        const getItem = (id: string): ItemType => {
            return itemsList[mappingItemIdIndex[id]];
        };

        const dragStrategyBetween = new DragStrategyBetween(rowsMap, itemsList, mappingItemIdIndex);
        const dragStrategyParent = new DragStrategyParent(rowsMap);

        let dragStrategy: DragStrategy | null;

        const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
            const dragOverItemId = DragStrategy.getItemId(e);
            const item = getItem(dragOverItemId);

            const isOwner = item?.ownerItemId === userId;
            const isAssignee = item?.assignee?.includes(userId);
            const access = accessCheck(
                permissionListScore,
                [
                    'work-item.all.create',
                    'work-item.all.edit',
                    'work-item.all.manage',
                    'work-item.owner.create',
                    'work-item.owner.edit',
                    'work-item.owner.manage',
                    'work-item.assignee.create',
                    'work-item.assignee.edit',
                    'work-item.assignee.manage',
                ],
                isOwner,
                isAssignee,
            );

            dragStrategy?.onDragOver(e, access);
        };

        const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
            dragStrategy?.onDragLeave(e);
        };

        const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
            dragStrategy?.onDragEnter(e);
        };

        const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
            const dragType = DragStrategy.getDragType(e);
            switch (dragType) {
                case 'between':
                    dragStrategy = dragStrategyBetween;
                    break;
                case 'parent':
                    dragStrategy = dragStrategyParent;
                    break;
                case 'none':
                default:
                    dragStrategy = null;
            }

            dragStrategy?.onDragStart(e);
            popoverRef.current?.hide();

            const item = getItem(DragStrategy.getItemId(e));
            startDragging(item, dragType);
        };

        const onDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
            const dragType = DragStrategy.getDragType(e);
            const dropItemId = dragStrategy?.onDragEnd(e);

            const dropItem = dropItemId ? getItem(dropItemId) : undefined;

            const item = getItem(DragStrategy.getItemId(e));

            finishDragging(item, dropItem, dragType);
        };

        return {
            onDragOver,
            onDragEnter,
            onDragLeave,
            onDragStart,
            onDragEnd,
        };
    }, [itemsList, mappingItemIdIndex, dispatch]);
};
